import { useState, useEffect, useRef, useCallback } from "react";

export const useGlobalDragState = () => {
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isDraggingRef = useRef(false);

  const resetDragState = useCallback(() => {
    dragCounter.current = 0;
    isDraggingRef.current = false;
    setIsDragging(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    isDraggingRef.current = isDragging;
  }, [isDragging]);

  useEffect(() => {
    const handleGlobalDragEnter = (e: DragEvent) => {
      // Only handle if dragging files
      if (e.dataTransfer?.types.includes("Files")) {
        e.preventDefault();
        dragCounter.current++;
        if (!isDraggingRef.current) {
          isDraggingRef.current = true;
          setIsDragging(true);
        }

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      }
    };

    const handleGlobalDragLeave = (e: DragEvent) => {
      if (e.dataTransfer?.types.includes("Files")) {
        e.preventDefault();
        dragCounter.current--;

        // Use a timeout to handle rapid enter/leave events
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
          if (dragCounter.current <= 0) {
            dragCounter.current = 0;
            isDraggingRef.current = false;
            setIsDragging(false);
            timeoutRef.current = null;
          }
        }, 100);
      }
    };

    const handleGlobalDrop = (e: DragEvent) => {
      dragCounter.current = 0;
      isDraggingRef.current = false;
      setIsDragging(false);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    const handleGlobalDragOver = (e: DragEvent) => {
      if (e.dataTransfer?.types.includes("Files")) {
        e.preventDefault();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // Reset drag state when ESC is pressed
      if (e.key === "Escape" && isDraggingRef.current) {
        resetDragState();
      }
    };

    const handleVisibilityChange = () => {
      // Reset drag state when tab becomes hidden (user switched tabs during drag)
      if (document.hidden && isDraggingRef.current) {
        resetDragState();
      }
    };

    const handleWindowBlur = () => {
      // Reset drag state when window loses focus (user dragged outside window)
      if (isDraggingRef.current) {
        resetDragState();
      }
    };

    // Add global listeners
    document.addEventListener("dragenter", handleGlobalDragEnter);
    document.addEventListener("dragleave", handleGlobalDragLeave);
    document.addEventListener("drop", handleGlobalDrop);
    document.addEventListener("dragover", handleGlobalDragOver);
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleWindowBlur);

    return () => {
      document.removeEventListener("dragenter", handleGlobalDragEnter);
      document.removeEventListener("dragleave", handleGlobalDragLeave);
      document.removeEventListener("drop", handleGlobalDrop);
      document.removeEventListener("dragover", handleGlobalDragOver);
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleWindowBlur);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [resetDragState]);

  return {
    isDragging,
    resetDragState,
  };
};
